import {
  useSubdistrictBulkDelete,
  useSubdistricts,
  useExportSubDistrict,
} from "@/api/subdistrict";
import { TableDefault } from "@/components/table/table-default";
import { TableForm } from "@/components/table/table-form";
import {
  DataConfig,
  useDefaultDataConfig,
} from "@/components/table/useDefaultDataConfig";
import { generateCustomSpec } from "@/utils/helper";
import {
  swalDelete,
  swalError,
  swalErrorDefault,
  swalSuccess,
} from "@/utils/lib/swal";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { ROLE } from "@/utils/constant";
import { useCurrentUser } from "../auth/useCurrentUser";
import { Badge } from "@/components/ui/badge.tsx";
import { DownloadButton } from "@/components/ui/button.tsx";
import { axiosInstance } from "@/utils/lib/axios.ts";
import { getBadgeVariant } from "@/utils/helper/getBadgeVariant";
import { getExceedDuration } from "@/utils/helper/getExceedDuration";
import { useProfileByKeycloakId } from "@/api/auth";
import { nullElement } from "@/components/null-element";

const customSpecAdmin = generateCustomSpec({
  path: "/api/crud/subdistrict",
  fields: [
    {
      label: "District Name",
      name: "subdistrictName",
      searchable: true,
      sortable: true,
    },
    {
      label: "City Name",
      name: "city.cityName",
      searchable: true,
      sortable: true,
    },
    {
      label: "District Code",
      name: "subdistrictCode",
      searchable: true,
      sortable: true,
    },
  ],
});

const customSpecNonAdmin = generateCustomSpec({
  path: "/api/crud/subdistrict",
  canBulk: false,
  fields: [
    {
      label: "District Name",
      name: "subdistrictName",
      searchable: true,
      sortable: true,
    },
    {
      label: "City Name",
      name: "city.cityName",
      searchable: true,
      sortable: true,
    },
    {
      label: "District Code",
      name: "subdistrictCode",
      searchable: true,
      sortable: true,
    },
  ],
});

const HeaderElement = ({
  dataConfig,
  initStatus,
}: {
  dataConfig: DataConfig;
  initStatus: string;
}) => {
  const [status, setStatus] = useState<string>("IDLE");
  const { mutate: exportList, isPending: isDownloading } =
    useExportSubDistrict();
  const { id: keycloakId } = useCurrentUser();
  const { data } = useProfileByKeycloakId({ keycloakId: keycloakId });
  const isDownloadSubDistrict =
    localStorage.getItem("isDownloadSubDistrict") ?? "";
  const stat = initStatus && isDownloadSubDistrict ? initStatus : status;

  const handleExport = () => {
    const params = {
      ...dataConfig,
      entityId: data?.id,
      page: undefined,
      limit: undefined,
    };

    setStatus("IN PROGRESS");
    exportList(
      {
        config: params,
        isDownload: isDownloadSubDistrict,
        onDownload: !!isDownloadSubDistrict,
      },
      {
        onSuccess: (response) => {
          setStatus(response.status);
        },
        onError: () => {
          swalErrorDefault.fire();
        },
      },
    );
  };

  return (
    <div className="flex items-center justify-end gap-2">
      <Badge variant={getBadgeVariant(stat)}>{stat}</Badge>&nbsp;
      <DownloadButton onClick={handleExport} loading={isDownloading} />
    </div>
  );
};

export function SubdistrictTable() {
  const [selectedRow, setSelectedRow] = useState<number[]>([]);
  const { dataConfig, setDataConfig } = useDefaultDataConfig();
  const tableForm = useForm();

  const { data: subdistricts } = useSubdistricts(dataConfig);
  const { mutate, isPending } = useSubdistrictBulkDelete();

  const { roles } = useCurrentUser();
  const isValidator = roles?.includes(ROLE.VALIDATOR_DOCUMENT_OFFICER);
  const isSysAdminView = roles?.includes(ROLE.SYS_ADMIN_VIEW);

  const [status, setStatus] = useState<string>("");
  const { mutate: exportList } = useExportSubDistrict();
  const { id: keycloakId } = useCurrentUser();
  const { data } = useProfileByKeycloakId({ keycloakId: keycloakId });

  useEffect(() => {
    const handleExport = async () => {
      const params = {
        ...dataConfig,
        entityId: data?.id,
        page: undefined,
        limit: undefined,
      };
      const isDownloadSubDistrict =
        localStorage.getItem("isDownloadSubDistrict") ?? "";

      if (isDownloadSubDistrict) {
        try {
          // Fetch duration from API
          const response = await axiosInstance.get(
            "/api/crud/master-param?paramType=ITV_ASYNC_IDLE&active=true",
          );

          if (getExceedDuration(isDownloadSubDistrict, response)) {
            localStorage.setItem("isDownloadSubDistrict", "");
            setStatus("IDLE");
          } else {
            exportList(
              {
                config: params,
                isDownload: isDownloadSubDistrict,
                onDownload: false,
              },
              {
                onSuccess: (response) => {
                  setStatus(response.status);
                },
                onError: () => {
                  swalErrorDefault.fire();
                },
              },
            );
          }
        } catch (error) {
          console.error("Error fetching duration:", error);
        }
      }
    };

    handleExport();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [exportList]);

  function handleBulkDelete() {
    swalDelete.fire({
      preConfirm: () => {
        mutate(
          { ids: selectedRow as unknown[] as string[] },
          {
            onSuccess: () => {
              swalSuccess.fire({
                text: "District has been deleted",
              });
            },
            onError: (error) => {
              swalError.fire({
                text: error.message,
              });
            },
            onSettled: () => {
              setSelectedRow([]);
            },
          },
        );
      },
    });
  }

  return (
    <>
      <TableForm
        setDataConfig={setDataConfig}
        form={tableForm}
        createPath={
          isValidator || isSysAdminView ? null : "/master-data/district/create"
        }
        selectedIds={selectedRow as unknown as string[]}
        handleBulkDelete={
          isValidator || isSysAdminView ? null : handleBulkDelete
        }
        isPendingDelete={isPending}
      />
      <TableDefault
        spec={isValidator ? customSpecNonAdmin : customSpecAdmin}
        data={subdistricts}
        selectedRow={selectedRow}
        setSelectedRow={setSelectedRow}
        dataConfig={dataConfig}
        setDataConfig={setDataConfig}
        headerElement={
          <HeaderElement dataConfig={dataConfig} initStatus={status} />
        }
        customButtonEdit={isSysAdminView ? nullElement : undefined}
        searchFilterByColumns={true}
      />
    </>
  );
}
